import { InternalWorkoutReview, InternalWorkoutReviewStatus,
         WorkoutSelection, WorkoutSelectionStatus } from '../../../entities';
import { WorkoutSelectionSerializer } from '../../serialization';
import { IWorkoutSelectionRepository } from '../interfaces';

export class DbWorkoutSelectionRepository implements IWorkoutSelectionRepository {
    constructor(protected db: any, protected logger: any) { }

    public async addReview(workoutId: string, review: InternalWorkoutReview): Promise<void> {
        const workout = await this.get(workoutId);
        if (workout != null) {
            workout.internalWorkoutReview = review;
            await this.update(workout);
        } else {
            throw Error(`Did not find workout with id ${workoutId} and could not add review`);
        }
    }

    public async addPowerReview(workoutId: string, review: InternalWorkoutReview): Promise<void> {
        const workout = await this.get(workoutId);
        if (workout != null) {
            workout.powerInternalWorkoutReview = review;
            await this.update(workout);
        } else {
            throw Error(`Did not find workout with id ${workoutId} and could not add review`);
        }
    }

    public async getAll(): Promise<WorkoutSelection[]> {
        const timeBefore = Date.now();
        const results: any = await this.db.allDocs({ include_docs: true });
        this.logger.info(`[DbWorkoutSelectionRepository] getAll took ${Date.now() - timeBefore} to complete`);
        const docs: any[] = results.rows.filter((doc: any) => !doc.doc._id.includes('_design'));

        const workoutSelection = docs.map((doc: any): WorkoutSelection => {
            return WorkoutSelectionSerializer.deserializeWorkoutSelection(doc.doc);
        });

        return workoutSelection;
    }

    public async filter(status: WorkoutSelectionStatus, from: number, to: number): Promise<WorkoutSelection[]> {
        const searchQueries = {
            [WorkoutSelectionStatus.ReviewFailed]: this.reviewFailedQuery(),
            [WorkoutSelectionStatus.ReviewPassed]: this.reviewPassedQuery(),
            [WorkoutSelectionStatus.AwatingReview]: this.awaitingReviewQuery(),
            [WorkoutSelectionStatus.WorkoutIncomplete]: this.workoutIncomplete(),
            [WorkoutSelectionStatus.ExerciseFaulty]: this.exerciseFaulty(),
        };

        const queryOptions = searchQueries[status];
        if (!queryOptions) {
            throw Error(`No workout selection status found for ${status}`);
        }

        const rangeQueryOptions = this.buildRangeQuery(queryOptions, from, to);
        const results = await this.db.find(rangeQueryOptions);
        const workoutSelection: WorkoutSelection[] = results.docs.map((doc: any): WorkoutSelection => {
            return WorkoutSelectionSerializer.deserializeWorkoutSelection(doc);
        });

        return workoutSelection;
    }

    public async add(workoutSelection: WorkoutSelection): Promise<WorkoutSelection> {
        const doc = WorkoutSelectionSerializer.serializeWorkoutSelection(workoutSelection);
        await this.db.put(doc);
        return this.db.get(workoutSelection.uniqueReference);
    }

    public async get(uid: string): Promise<WorkoutSelection | undefined> {
        try {
            const doc = await this.db.get(uid);
            return WorkoutSelectionSerializer.deserializeWorkoutSelection(doc);
        } catch (err) {
            if (err.status === 404) {
                return undefined;
            } else {
                throw err;
            }
        }
    }

    public async update(workoutSelection: WorkoutSelection): Promise<WorkoutSelection> {
        const old = await this.get(workoutSelection.uniqueReference);
        let lastRevisionToAvoidUpdateConflicts;
        if (old != null) {
            lastRevisionToAvoidUpdateConflicts = (old as any)._rev;
        }

        const doc =
            WorkoutSelectionSerializer.serializeWorkoutSelection(
                workoutSelection, lastRevisionToAvoidUpdateConflicts);

        await this.db.put(doc);
        return this.db.get(workoutSelection.uniqueReference);
    }

    private reviewFailedQuery(): any {
        return { internalWorkoutReview: {
            reviewStatus: InternalWorkoutReviewStatus.Fail,
        } };
    }

    private reviewPassedQuery(): any {
        return { internalWorkoutReview: {
            reviewStatus: InternalWorkoutReviewStatus.Pass,
        }};
    }

    private awaitingReviewQuery(): any {
        return { internalWorkoutReview: {
            reviewStatus: InternalWorkoutReviewStatus.NeedReview,
        } };
    }

    private workoutIncomplete(): any {
        return { exerciseSelections: {
            $elemMatch: {
                approved: false,
            },
        } };
    }

    private exerciseFaulty(): any {
        return { exerciseSelections: {
            $elemMatch: {
                $or: [
                { 'exercise.disable': true},
                { 'alternateExercise.disable': true }],
            },
        } };
    }

    private buildRangeQuery(queryOptions: any, from: number, to: number): any {
        return {
            selector: {
                ...queryOptions,
                $and: [
                    { workoutSequenceDate: { $gte: from } },
                    { workoutSequenceDate: { $lte: to } },
                ],
            },
            limit: Number.MAX_SAFE_INTEGER,
        };
    }
}
